/**
 * Authentication Context
 * 
 * Manages authentication state across the React app,
 * like keeping track of which pirates have the right to board the ship!
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';  // Legacy role for compatibility
  userRole?: string;  // New role system: treeline_user, admin, owner
  capabilities?: {
    canUseTeaching: boolean;
    canManageUsers: boolean;
    canManageContentSources: boolean;
  };
  orgId?: string;  // Organization ID from Treeline authentication
  isZTRUser?: boolean;  // Flag for ZTR organization users
  impersonation?: {
    isImpersonating: boolean;
    actualRole: string;
    effectiveRole: string;
    effectiveAudience?: 'customer' | 'internal';
    canImpersonate: boolean;
    canImpersonateAudience: boolean;
    availableRoles: string[];
  };
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (user: AuthUser) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  startImpersonation: (targetRole: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  startAudienceImpersonation: (targetAudience: 'customer') => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;

  // Check authentication status on mount and when needed
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      
      console.log('🔍 AuthContext: Checking authentication status...');
      console.log('🍪 Available cookies:', document.cookie);
      
      // Check authentication status via Treeline cookies (no token needed)
      const response = await fetch('/api/auth/status', {
        credentials: 'include' // Include cookies for Treeline authentication
      });
      
      console.log('📡 AuthContext: Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 AuthContext: Response data:', data);
        
        if (data.success && data.data.isAuthenticated && data.data.user) {
          console.log('✅ AuthContext: User authenticated:', data.data.user);
          setUser(data.data.user);
        } else {
          console.log('❌ AuthContext: User not authenticated');
          setUser(null);
        }
      } else {
        console.log('❌ AuthContext: Request failed');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ AuthContext: Auth check error:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (userData: AuthUser) => {
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
  };

  const startImpersonation = async (targetRole: string) => {
    try {
      console.log('🎭 Starting impersonation as:', targetRole);
      
      const response = await fetch('/api/auth/impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ targetRole })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start impersonation');
      }

      // Refresh auth state to get updated impersonation info
      await checkAuth();
      console.log('✅ Impersonation started successfully');
    } catch (error) {
      console.error('❌ Failed to start impersonation:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      console.log('🎭 Stopping impersonation...');
      
      const response = await fetch('/api/auth/impersonate', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to stop impersonation');
      }

      // Refresh auth state to get updated role info
      await checkAuth();
      console.log('✅ Impersonation stopped successfully');
    } catch (error) {
      console.error('❌ Failed to stop impersonation:', error);
      throw error;
    }
  };

  const startAudienceImpersonation = async (targetAudience: 'customer') => {
    try {
      console.log('👥 Starting audience impersonation as:', targetAudience);
      
      const response = await fetch('/api/auth/impersonate/audience', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ targetAudience })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start audience impersonation');
      }

      // Refresh auth state to get updated impersonation info
      await checkAuth();
      console.log('✅ Audience impersonation started successfully');
    } catch (error) {
      console.error('❌ Failed to start audience impersonation:', error);
      throw error;
    }
  };

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    startImpersonation,
    stopImpersonation,
    startAudienceImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;